import React, { useState, useEffect } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { ClerkProvider, SignedIn, SignedOut } from '@clerk/clerk-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import Dashboard from './components/Dashboard';
import Login from './components/Login';
import EmailDomainValidator from './components/EmailDomainValidator';
import { ToastProvider } from './contexts/ToastContext';
import { ConfirmDialogProvider } from './contexts/ConfirmDialogContext';

// Get Clerk Publishable Key from environment variables
const clerkPubKey = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY || '';
const useClerk = clerkPubKey && clerkPubKey.startsWith('pk_');

// Create a QueryClient with optimized settings 
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Global defaults for all queries
      staleTime: 15 * 60 * 1000, // 15 minutes 
      gcTime: 30 * 60 * 1000, // 30 minutes - keep in cache
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 2,
    },
  },
});

function App() {
  // If Clerk is not configured, use simple localStorage auth
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    if (!useClerk) {
      // Simple auth check for non-Clerk mode
      const authenticated = localStorage.getItem('supply_chain_authenticated') === 'true';
      setIsAuthenticated(authenticated);
      setIsLoading(false);
    }
  }, []);

  const handleLogin = () => {
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('supply_chain_authenticated');
    localStorage.removeItem('supply_chain_user');
    setIsAuthenticated(false);
  };

  // Loading state for non-Clerk mode
  if (!useClerk && isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // If Clerk is configured, use Clerk authentication
  if (useClerk) {
    return (
      <ClerkProvider 
        publishableKey={clerkPubKey}
        appearance={{
          layout: {
            socialButtonsPlacement: 'bottom',
            socialButtonsVariant: 'iconButton',
          },
          variables: {
            colorPrimary: '#2563eb', 
            colorBackground: '#ffffff',
            colorText: '#1f2937',
            colorTextSecondary: '#6b7280',
            colorInputBackground: '#ffffff',
            colorInputText: '#1f2937',
            borderRadius: '0.5rem',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
          },
          elements: {
            rootBox: {
              width: '100%',
              maxWidth: '400px',
            },
            card: {
              backgroundColor: '#ffffff',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              border: 'none',
              borderRadius: '1rem',
              padding: '2rem',
            },
            headerTitle: {
              fontSize: '1.5rem',
              fontWeight: '700',
              color: '#1f2937',
                backgroundColor: 'transparent',
            },
            headerSubtitle: {
              color: '#6b7280',
              fontSize: '0.875rem',
            },
            formButtonPrimary: {
              backgroundColor: '#2563eb',
              color: '#ffffff',
              fontWeight: '500',
              fontSize: '1rem',
              padding: '0.75rem 1rem',
              borderRadius: '0.5rem',
              '&:hover': {
                backgroundColor: '#1d4ed8',
              },
            },
            formFieldInput: {
              borderColor: '#d1d5db',
              borderRadius: '0.5rem',
              padding: '0.75rem 1rem',
              fontSize: '1rem',
              '&:focus': {
                borderColor: '#2563eb',
                boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.1)',
              },
            },
            footerActionLink: {
              color: '#2563eb',
              fontWeight: '500',
              '&:hover': {
                color: '#1d4ed8',
              },
            },
            socialButtonsBlockButton: {
              backgroundColor: '#ffffff',
              border: '1px solid #d1d5db',
              color: '#374151',
              borderRadius: '0.5rem',
              padding: '0.75rem 1rem',
              fontSize: '0.875rem',
              '&:hover': {
                backgroundColor: '#f9fafb',
                borderColor: '#2563eb',
              },
            },
            dividerLine: {
              backgroundColor: '#e5e7eb',
            },
            dividerText: {
              color: '#9ca3af',
              fontSize: '0.875rem',
            },
            formFieldLabel: {
              color: '#374151',
              fontWeight: '500',
              fontSize: '0.875rem',
            },
            identityPreviewText: {
              color: '#1f2937',
            },
            identityPreviewEditButton: {
              color: '#2563eb',
            },
          },
        }}
      >
        <QueryClientProvider client={queryClient}>
          <ToastProvider>
            <ConfirmDialogProvider>
            <BrowserRouter>
              <EmailDomainValidator allowedDomain="bublishing.com">
                <div className="min-h-screen bg-white">
                  {/* Show Dashboard when user is signed in */}
                  {/* @ts-ignore - Clerk React 18 type compatibility */}
                  <SignedIn>
                    <Dashboard />
                  </SignedIn>
                  
                  {/* Show Login when user is signed out */}
                  {/* @ts-ignore - Clerk React 18 type compatibility */}
                  <SignedOut>
                    <Login />
                  </SignedOut>
                </div>
              </EmailDomainValidator>
            </BrowserRouter>
            </ConfirmDialogProvider>
          </ToastProvider>
          {/* Show dev tools only in development */}
          {process.env.NODE_ENV === 'development' && (
            <ReactQueryDevtools initialIsOpen={false} />
          )}
        </QueryClientProvider>
      </ClerkProvider>
    );
  }

  // Fallback: Simple localStorage authentication (no Clerk)
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <ConfirmDialogProvider>
        <BrowserRouter>
          <div className="min-h-screen bg-white">
            {isAuthenticated ? (
              <Dashboard onLogout={handleLogout} />
            ) : (
              <Login onLogin={handleLogin} />
            )}
          </div>
        </BrowserRouter>
        </ConfirmDialogProvider>
      </ToastProvider>
      {/* Show dev tools only in development */}
      {process.env.NODE_ENV === 'development' && (
        <ReactQueryDevtools initialIsOpen={false} />
      )}
    </QueryClientProvider>
  );
}

export default App;
